import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Person as PersonIcon,
  Email as EmailIcon
} from '@mui/icons-material';

interface OwnersDialogProps {
  open: boolean;
  onClose: () => void;
  application: {
    id: string;
    name: string;
    primary_owners?: Array<{
      id: string;
      username?: string;
      email?: string;
      first_name?: string;
      last_name?: string;
    }>;
    secondary_owners?: Array<{
      id: string;
      username?: string;
      email?: string;
      first_name?: string;
      last_name?: string;
    }>;
    delegates?: Array<{
      id: string;
      username?: string;
      email?: string;
      first_name?: string;
      last_name?: string;
      delegated_at?: string;
      reason?: string;
      business_justification?: string;
      status?: string;
    }>;
  };
}

const OwnersDialog: React.FC<OwnersDialogProps> = ({
  open,
  onClose,
  application
}) => {
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    // You could add a snackbar notification here
  };

  const getOwnerName = (owner: any) => {
    if (owner.first_name && owner.last_name) {
      return `${owner.first_name} ${owner.last_name}`;
    }
    return owner.username || 'Unknown';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Owners - {application.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Primary Owners */}
        {application.primary_owners?.length && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" fontSize="small" />
              Primary Owners
            </Typography>
            {application.primary_owners.map((owner) => (
              <Box key={owner.id} sx={{ mb: 1, p: 1, bgcolor: 'primary.50', borderRadius: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {getOwnerName(owner)}
                  </Typography>
                  <Chip label="Primary" color="primary" size="small" />
                </Box>
                {owner.email && (
                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                      {owner.email}
                    </Typography>
                    <Tooltip title="Copy Email">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(owner.email!, 'Email')}
                        sx={{ p: 0.5 }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Secondary Owners */}
        {application.secondary_owners?.length && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="action" fontSize="small" />
              Secondary Owners
            </Typography>
            {application.secondary_owners.map((owner) => (
              <Box key={owner.id} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {getOwnerName(owner)}
                  </Typography>
                  <Chip label="Secondary" color="default" size="small" variant="outlined" />
                </Box>
                {owner.email && (
                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                      {owner.email}
                    </Typography>
                    <Tooltip title="Copy Email">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(owner.email!, 'Email')}
                        sx={{ p: 0.5 }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* Delegates */}
        {application.delegates?.length && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="secondary" fontSize="small" />
              Delegates
            </Typography>
            {application.delegates.map((delegate) => (
              <Box key={delegate.id} sx={{ mb: 1, p: 1, bgcolor: 'secondary.50', borderRadius: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="medium">
                    {getOwnerName(delegate)}
                  </Typography>
                  <Chip label="Delegate" color="secondary" size="small" />
                  {delegate.status && (
                    <Chip 
                      label={delegate.status} 
                      size="small" 
                      variant="outlined"
                      color={delegate.status === 'active' ? 'success' : 'default'}
                    />
                  )}
                </Box>
                {delegate.email && (
                  <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                      {delegate.email}
                    </Typography>
                    <Tooltip title="Copy Email">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(delegate.email!, 'Email')}
                        sx={{ p: 0.5 }}
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {delegate.reason && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    <strong>Reason:</strong> {delegate.reason}
                  </Typography>
                )}
                {delegate.business_justification && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    <strong>Business Justification:</strong> {delegate.business_justification}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* No Owners */}
        {(!application.primary_owners?.length && !application.secondary_owners?.length && !application.delegates?.length) && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            No owners or delegates assigned to this application
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OwnersDialog;
